import { newAmount } from '@orus.eu/amount'

import type { PossibleInsuranceLimitOfIndemnity } from '../../../model/policy-item.ts'
import type { FrAupaPolicyItem } from '../fr-aupa-insurance.ts'

const expertValue: PossibleInsuranceLimitOfIndemnity = { type: 'text', value: 'Valeur déterminée par un expert' }

const assistanceAgreement: PossibleInsuranceLimitOfIndemnity = {
  type: 'text',
  value: 'Voir convention d’assistance',
}

export type FrAupaLimitOfIndemnityEntry = {
  limit: PossibleInsuranceLimitOfIndemnity
  additionalInfo: string | null
  /**
   * The « mise en avant » column of the product sheet, when it is not what the guarantees table would show on its
   * own: the text, and the part it sums up when the ceiling has several.
   */
  inGuaranteesTable?: { text: string; part?: string }
}

/**
 * Transcription of the "Possible limit of indemnities" and "Additional info" columns of the GUARANTEES tab of the
 * product sheet. No limit depends on the state: the extended driver cover offers two ceilings, and which one
 * applies is the trigger's business, read from the ceiling the customer picked on the cover.
 */
export const frAupaLimitOfIndemnityByPolicyItem_v1_0_0 = {
  civilLiability: {
    inGuaranteesTable: { text: 'Dommages matériels : 100 000 000 €', part: 'Dommages matériels' },
    limit: {
      type: 'multipleLimits',
      limits: [
        { label: 'Dommages corporels', limit: { type: 'text', value: 'Illimité' } },
        { label: 'Dommages matériels', limit: { type: 'fixedAmount', value: newAmount(100_000_000) } },
      ],
    },
    additionalInfo: null,
  },
  operatingLiability: {
    inGuaranteesTable: { text: 'Dommages matériels : 1 000 000 €', part: 'Dommages matériels' },
    limit: {
      type: 'multipleLimits',
      limits: [
        { label: 'Dommages corporels', limit: { type: 'fixedAmount', value: newAmount(10_000_000) } },
        {
          label: 'Faute inexcusable de l’employeur',
          limit: {
            type: 'perClaimAndAnnualAmounts',
            perClaimLimit: newAmount(1_000_000),
            annualLimit: newAmount(2_000_000),
          },
        },
        { label: 'Dommages matériels', limit: { type: 'fixedAmount', value: newAmount(1_000_000) } },
        { label: 'Dommages immatériels consécutifs', limit: { type: 'fixedAmount', value: newAmount(500_000) } },
      ],
    },
    additionalInfo: null,
  },
  ecologicalDamage: { limit: { type: 'fixedAmount', amount: newAmount(1_300_000) }, additionalInfo: null },
  criminalDefenseAndRecourse: {
    inGuaranteesTable: { text: '20 000 € par litige' },
    limit: { type: 'fixedAmount', amount: newAmount(20_000) },
    additionalInfo: null,
  },
  driverProtection: { limit: { type: 'fixedAmount', amount: newAmount(250_000) }, additionalInfo: null },
  glass: {
    inGuaranteesTable: { text: 'Frais de réparation / remplacement' },
    limit: { type: 'text', value: 'Frais de réparation et de remplacement : illimités' },
    additionalInfo: null,
  },
  naturalDisasters: { limit: expertValue, additionalInfo: null },
  technologicalDisasters: { limit: expertValue, additionalInfo: null },
  climateEvents: { limit: expertValue, additionalInfo: null },
  terrorism: { limit: expertValue, additionalInfo: null },
  fire: { limit: expertValue, additionalInfo: null },
  theft: { limit: expertValue, additionalInfo: null },
  allAccidentDamage: { limit: expertValue, additionalInfo: null },
  enhancedCompensation: {
    inGuaranteesTable: { text: 'Jusqu’à la valeur d’achat', part: 'Véhicule détenu en propre depuis moins de 2 ans' },
    limit: {
      type: 'multipleLimits',
      limits: [
        { label: 'Véhicule détenu en propre depuis moins de 2 ans', limit: { type: 'text', value: 'Valeur d’achat' } },
        {
          label: 'Véhicule détenu en propre depuis 2 ans et plus',
          limit: {
            type: 'text',
            value: 'Valeur à dire d’expert majorée de 20 %, minimum 1 000 €, dans la limite de la valeur d’achat',
          },
        },
        {
          label: 'Véhicule en LLD ou LOA',
          limit: {
            type: 'text',
            value:
              'Créance du loueur dans la limite de la valeur à dire d’expert, apport initial remboursé jusqu’à 5 000 € avec abattement à compter de la 3e année',
          },
        },
      ],
    },
    additionalInfo:
      'En cas de vol ou de destruction de votre véhicule, l’indemnisation habituelle est la valeur à dire d’expert. Cette option la renforce : elle vous verse davantage si vous êtes propriétaire, et elle solde votre engagement auprès du loueur si votre véhicule est en location.',
  },
  extendedDriver: {
    inGuaranteesTable: { text: 'Jusqu’à 1 000 000 €' },
    limit: { type: 'amounts', amounts: [newAmount(500_000), newAmount(1_000_000)] },
    additionalInfo: null,
  },
  vehicleEquipment: { limit: { type: 'fixedAmount', amount: newAmount(20_000) }, additionalInfo: null },
  vehicleContents: {
    limit: { type: 'fixedAmount', amount: newAmount(5_000) },
    additionalInfo: 'Sans dépasser la valeur des biens neufs, vétusté déduite',
  },
  reducedDeductible: { limit: { type: 'none' }, additionalInfo: null },
  assistance: { limit: assistanceAgreement, additionalInfo: null },
} as const satisfies Record<FrAupaPolicyItem, FrAupaLimitOfIndemnityEntry>
